// terminVorlage.ts — „Duplizieren…" im Kalender-Kontextmenü (T-269).
//
// Ein bestehender Termin dient als Vorlage für einen NEUEN: Das Termin-Fenster
// öffnet sich im Anlage-Modus, vorbelegt mit den Angaben des Originals, und
// legt beim Speichern über den normalen Create-Pfad an — nie ein PATCH auf die
// Vorlage. Hier steckt nur die reine Übersetzung „geladener Termin →
// Formular-Startwerte", ohne React/DOM, damit sie unter vitest testbar bleibt.
//
// Bewusst NICHT übernommen:
// - Notizen, Dokumente, Mail-Verknüpfungen, To-do-Bindung — Sidecar-Inhalte
//   des Originals;
// - der Body einer Teams-Besprechung — er trägt den Beitrittslink des ALTEN
//   Meetings; stattdessen wird „Online-Meeting" angehakt, Graph erzeugt einen
//   neuen Link;
// - die Serie — das Duplikat ist ein Einzeltermin zur Zeit der gewählten
//   Instanz.

import type { CalendarEventDetail } from "../../services/calendarService";
import type { TerminKontaktDto, TerminMetaDto } from "../../services/calendarTerminService";
import type { ReminderPreset } from "./RecurrencePicker";
import { minutesToPreset } from "./terminFormShared";
import { istVertraulich } from "./terminPrivatAnzeige";
import type { CalendarGridEvent } from "./types";

/** Was der Öffner über die Vorlage mitgibt (Postfach + Id zum Nachladen). */
export interface TerminVorlage {
    mailbox: string;
    eventId: string;
    /** Titel des Originals — für den Hinweis „Kopie von …". */
    titel: string;
    /** Ort laut Raster — Rückfall, wenn der Termin nicht nachgeladen werden kann. */
    location: string;
    /**
     * Termin der Ebene „Termine meiner Kontakte": Postfach ohne Leserecht, das
     * Backend lehnt getEvent/getMeta ab — dann zählen nur die Raster-Daten.
     */
    ueberKontakt?: boolean;
}

/** Formular-Startwerte aus der Vorlage (Zeiten kommen separat aus dem Raster). */
export interface TerminVorlageWerte {
    subject: string | null;
    location: string;
    body: string;
    bodyContentType: string | null;
    isOnlineMeeting: boolean;
    attendees: { address: string; name: string }[];
    roomAddress: string;
    reminderPreset: ReminderPreset;
    istPrivat: boolean;
    /** null ⇒ Art nicht übernehmen (unklassifiziert, Abwesenheit, Block). */
    terminArt: "kundentermin" | "austausch" | "sonstige" | null;
    kontakte: { id: number; name: string }[];
    farbe: string | null;
    vorgang: { id: number; titel: string | null } | null;
}

/**
 * Geladener Termin (+ Sidecar + Kontaktzuweisung) → Startwerte des
 * Anlage-Formulars. Jede Quelle darf fehlen (kein Zugriff, Ladefehler) — dann
 * bleiben die zugehörigen Felder leer, und der Rest kommt aus der Vorlage
 * selbst (Raster-Ort).
 */
export function vorlageAusTermin(
    vorlage: Pick<TerminVorlage, "location">,
    detail: CalendarEventDetail | null,
    meta: TerminMetaDto | null,
    kontakte: TerminKontaktDto[] | null,
): TerminVorlageWerte {
    const roh = detail?.attendees ?? [];
    const online = !!(detail?.onlineMeetingUrl || detail?.isOnlineMeeting);
    const art = meta?.terminArt;

    // Hauptkontakt zuerst; ohne Zuweisung reicht der `kundeId`-Rückfall nur
    // mit Namen — den kennt hier niemand, also bleibt die Liste dann leer.
    const sortiert = [...(kontakte ?? [])].sort((a, b) => Number(b.haupt) - Number(a.haupt));

    return {
        subject: detail?.subject ?? null,
        location: (detail?.location ?? vorlage.location ?? "").trim(),
        body: online ? "" : detail?.body?.content ?? "",
        bodyContentType: online ? null : detail?.body?.contentType ?? null,
        isOnlineMeeting: online,
        attendees: roh
            .filter((a) => a.type !== "resource" && !!a.address)
            .map((a) => ({ address: a.address, name: a.name?.trim() || a.address })),
        roomAddress: roh.find((a) => a.type === "resource")?.address ?? "",
        reminderPreset: detail ? minutesToPreset(detail.isReminderOn, detail.reminderMinutesBeforeStart) : "none",
        istPrivat: istVertraulich(detail?.sensitivity),
        terminArt: art === "kundentermin" || art === "austausch" || art === "sonstige" ? art : null,
        kontakte: sortiert.map((k) => ({ id: k.contactId, name: k.name })),
        farbe: meta?.farbe ?? null,
        vorgang: meta?.vorgangId != null ? { id: meta.vorgangId, titel: meta.vorgangTitel ?? null } : null,
    };
}

/**
 * Darf der Termin als Vorlage dienen? Nicht, wenn er für den Betrachter
 * maskiert ist (fremder privater Termin, T-220/PO 27.07.2026): Kopiert würde
 * nur die Maske „Privat" — ohne Ort und Teilnehmer.
 */
export function kannDuplizieren(
    ev: Pick<CalendarGridEvent, "sensitivity">,
    eigenesPostfach: boolean,
): boolean {
    return eigenesPostfach || !istVertraulich(ev.sensitivity);
}
